import Image from '@/components/Image'
import { cn } from '@/lib/utils'
import type { Event } from '@nostr/tools/wasm'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { BadgeSlot, parseBadgePairs } from './badge-utils'

/**
 * NIP-58 kind 30008 badge set (categorized group of accepted badges).
 * NOTE: kind 30008 with `d` == "profile_badges" is the deprecated profile
 * badges event and is rendered by ProfileBadges instead.
 */
export default function BadgeSet({ event, className }: { event: Event; className?: string }) {
  const { t } = useTranslation()
  const d = event.tags.find((tag) => tag[0] === 'd')?.[1]
  const title = event.tags.find((tag) => tag[0] === 'title')?.[1] || d
  const image = event.tags.find((tag) => tag[0] === 'image')?.[1]
  const description = event.tags.find((tag) => tag[0] === 'description')?.[1]
  const { pairs } = useMemo(() => parseBadgePairs(event.tags), [event])

  return (
    <div className={cn('space-y-3', className)}>
      <div className="text-sm font-medium text-muted-foreground">{t('Badge set')}</div>
      {(title || image || description) && (
        <div className="space-y-2">
          {image && (
            <Image
              imageUrl={image}
              imagePubkey={event.pubkey}
              alt={title}
              classNames={{ wrapper: 'max-h-40 w-full rounded-lg bg-muted' }}
            />
          )}
          {title && <h2 className="truncate text-xl font-semibold">{title}</h2>}
          {description && (
            <p className="whitespace-pre-wrap text-sm text-muted-foreground">{description}</p>
          )}
        </div>
      )}
      {pairs.length === 0 ? (
        <div className="text-sm text-muted-foreground">{t('Empty list.')}</div>
      ) : (
        <div className="grid grid-cols-1 gap-1 sm:grid-cols-2">
          {pairs.map((pair, i) => (
            <BadgeSlot
              key={`${pair.definition.kind}:${pair.definition.pubkey}:${pair.definition.identifier}:${pair.award?.id ?? i}`}
              definition={pair.definition}
              award={pair.award}
            />
          ))}
        </div>
      )}
    </div>
  )
}
